import { useState } from 'react';
import type { CategorizedGates, GateIssue } from '../../../../coordinators/rewards/categorizeGates';

const DISCORD_SUPPORT_URL = 'https://discord.gg/playcast';

interface SeveralIssuesModalProps {
  categorized: CategorizedGates;
  onDismiss: () => void;
}

export const SeveralIssuesModal = ({ categorized, onDismiss }: SeveralIssuesModalProps) => {
  const isHardStop = categorized.contactSupport.length > 0;
  const rows = buildRankedRows(categorized);
  const [expandedIdx, setExpandedIdx] = useState<number>(0);

  return (
    <>
      <div className='absolute inset-0 z-40 rounded-[12px] bg-[#181818] opacity-[0.72]' />
      <div className='absolute inset-0 z-50 flex items-center justify-center p-4'>
        <div className='w-full rounded-[10px] border border-[#e4e4e4]/12 bg-[#181818] p-5'>
          <div className='flex w-full flex-col items-center gap-3'>
            <p className='m-0 text-center text-[20px] font-[590] leading-[26px] text-[#e4e4e4]/92'>
              {isHardStop ? "Can't start Host Mode" : 'Finish these first'}
            </p>

            <div className='w-full max-h-[180px] overflow-y-auto rounded-[8px] border border-[#e4e4e4]/12'>
              {rows.map((row, idx) => (
                <div
                  key={row.key}
                  className={`border-b border-[#e4e4e4]/8 last:border-b-0 ${row.variant === 'contact-support' ? 'bg-[rgba(239,68,68,0.14)]' : ''}`}
                >
                  <button
                    type='button'
                    onClick={() => setExpandedIdx(expandedIdx === idx ? -1 : idx)}
                    className='flex w-full items-center gap-2 px-3 py-2 border-none bg-transparent text-left cursor-pointer'
                  >
                    <RowIcon variant={row.variant} />
                    <span className='flex-1 text-[13px] font-medium leading-[18px] text-[#e4e4e4]/80'>
                      {row.label}
                    </span>
                    <span className='text-[10px] text-[#e4e4e4]/37'>
                      {expandedIdx === idx ? '▲' : '▼'}
                    </span>
                  </button>
                  {expandedIdx === idx && row.detail && (
                    <div className='px-3 pb-2 text-[12px] leading-[16px] text-[#e4e4e4]/55'>
                      {row.detail}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {isHardStop ? (
              <>
                <a
                  href={DISCORD_SUPPORT_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex w-full min-h-[44px] items-center justify-center rounded-full border-none bg-[#FBBF24] px-4 py-3 text-[15px] font-semibold text-[#141414] cursor-pointer no-underline transition-transform hover:scale-[1.02] active:scale-[0.98]'
                >
                  Get help
                </a>
                <button
                  type='button'
                  onClick={onDismiss}
                  className='flex w-full items-center justify-center rounded-full border border-transparent bg-transparent px-1.5 py-0 text-[14px] font-medium leading-[20px] text-[#e4e4e4]/55 cursor-pointer transition-colors hover:text-[#e4e4e4]/80'
                  style={{ height: 24 }}
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <button
                  type='button'
                  disabled
                  className='w-full min-h-[44px] rounded-full border-none bg-[#FBBF24] px-4 py-3 text-[15px] font-semibold text-[#141414] opacity-50 cursor-not-allowed'
                >
                  Enter Host Mode
                </button>
                <button
                  type='button'
                  onClick={onDismiss}
                  className='flex w-full items-center justify-center rounded-full border border-transparent bg-transparent px-1.5 py-0 text-[14px] font-medium leading-[20px] text-[#e4e4e4]/55 cursor-pointer transition-colors hover:text-[#e4e4e4]/80'
                  style={{ height: 24 }}
                >
                  Cancel Host Mode
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

type RowVariant = 'contact-support' | 'please-wait' | 'user-action' | 'reboot' | 'apps';

interface RankedRow {
  key: string;
  variant: RowVariant;
  label: string;
  detail: string;
}

function buildRankedRows(cats: CategorizedGates): RankedRow[] {
  const rows: RankedRow[] = [];

  for (const issue of cats.contactSupport) {
    rows.push({ key: issue.gateId, variant: 'contact-support', label: issue.label, detail: issue.detail });
  }
  for (const issue of cats.pleaseWait) {
    rows.push({ key: issue.gateId, variant: 'please-wait', label: issue.label, detail: issue.detail });
  }
  for (const issue of cats.userActions) {
    if (issue.actionKind === 'reboot') {
      rows.push({ key: issue.gateId, variant: 'reboot', label: issue.label, detail: issue.detail });
    } else {
      rows.push({ key: issue.gateId, variant: 'user-action', label: issue.label, detail: issue.detail });
    }
  }
  if (cats.appsHolding.length > 0) {
    rows.push({
      key: 'apps-holding',
      variant: 'apps',
      label: `${cats.appsHolding.length} fullscreen app${cats.appsHolding.length > 1 ? 's' : ''}`,
      detail: cats.appsHolding.join(', '),
    });
  }

  return rows;
}

function RowIcon({ variant }: { variant: RowVariant }) {
  if (variant === 'contact-support') {
    return <span className='flex-shrink-0 w-2 h-2 rounded-full bg-red-500' />;
  }
  if (variant === 'please-wait') {
    return (
      <span
        className='flex-shrink-0 w-3 h-3 rounded-full border-2 border-[#e4e4e4]/20 border-t-[#FBBF24]'
        style={{ animation: 'spin 0.8s linear infinite' }}
      />
    );
  }
  if (variant === 'user-action') {
    return <span className='flex-shrink-0 w-2 h-2 rounded-full bg-red-500' />;
  }
  return null;
}
